import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  enableBodyScroll,
  disableBodyScroll,
} from '@hh/global.js'

const CLOSE_DURATION_MS = 300
const HOVER_CLOSE_DELAY_MS = 300
const HOVER_CLOSE_DURATION_MS = 300
const HOVER_CLOSE_TIMEOUT_MS = 200

class HHMegaMenuModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: false,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
    hasOpenClass: {
      type: Boolean,
      attribute: 'data-panel-open',
      reflect: true,
    },
    focused: {
      reflect: true,
    },
    mousedown: {
      reflect: true,
    },
    hoverEnabled: {
      type: Boolean,
      attribute: 'hover-enabled',
    },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 750px)'
    this.desktopBreakpoint = '(min-width: 990px)'
    this.bodyListener = this.bodyListener.bind(this)
    this.hoverEnabled = false
    this.hoverCloseTimeout = null
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('closeModal', this.close.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.clearHoverCloseTimeout()
  }

  firstUpdated() {
    this.showMenuOnHover()
  }

  showMenuOnHover() {
    if (this.hoverEnabled && this.isDesktop()) {
      this.$container.value.addEventListener('mouseenter', () => {
        clearTimeout(this.hoverCloseTimeout)
        this.hoverCloseTimeout = null
        if (!this.open) this.toggle()
      })

      this.$container.value.addEventListener('mouseleave', () => {
        this.hoverCloseTimeout = setTimeout(() => {
          this.hoverCloseTimeout = null
          if (this.open) this.toggle(undefined, false)
        }, HOVER_CLOSE_TIMEOUT_MS)
      })
    }
  }

  clearHoverCloseTimeout() {
    if (this.hoverCloseTimeout) {
      clearTimeout(this.hoverCloseTimeout)
      this.hoverCloseTimeout = null
    }
  }

  updated() {
    if (this.isMobile()) {
      this.setBodyScroll(!this.open)
    }
  }

  requestUpdate(name, value) {
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    e && e.preventDefault()

    const isOpen = typeof value === 'undefined' ? !this.open : value
    if (isOpen) {
      this.open = true
      this.hasOpenClass = true
      this.classList.add('panel-open')
      document.body.classList.remove('mini-cart-is-active')
      requestAnimationFrame(() => {
        this.addBodyListener()
        this.lockHeight && this.setHeight()
        this.broadcast()
      })
    } else {
      this.clearHoverCloseTimeout()
      const isHoverClose = this.hoverEnabled && this.isDesktop()
      this.hasOpenClass = false
      this.classList.remove('panel-open')
      this.removeAttribute('data-panel-open')
      this.removeBodyListener()
      if (!isHoverClose) this.broadcast()
      this.handleCloseAnimation(isHoverClose)
    }
  }

  handleCloseAnimation(broadcastOnComplete = false) {
    const totalMs =
      this.hoverEnabled && this.isDesktop()
        ? HOVER_CLOSE_DELAY_MS + HOVER_CLOSE_DURATION_MS
        : CLOSE_DURATION_MS
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }
      const elapsedTime = time - animationStart
      if (elapsedTime < totalMs) {
        window.requestAnimationFrame(handleAnimation)
      } else {
        if (broadcastOnComplete) this.broadcast()
        this.open = false
      }
    }

    window.requestAnimationFrame(handleAnimation)
  }

  closeFromDetails(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
  }

  close() {
    this.open && this.toggle()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  isDesktop() {
    return window.matchMedia(this.desktopBreakpoint).matches
  }

  setBodyScroll(enable = true) {
    if (enable) {
      document.body.style.position = ''
      document.body.style.overflow = ''
    } else {
      document.body.style.position = 'fixed'
      document.body.style.overflow = 'hidden'
    }
  }

  addBodyListener() {
    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(e) {
    !e._intercepted && this.open && this.toggle()
  }

  setHeight() {
    setTimeout(this.initHeight.bind(this), 0)
  }

  initHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100dvh - ${Math.floor(
          parentTop + detailsTop - this.heightOffset
        )}px)`
      : ''

    document.documentElement.style.setProperty(
      '--header-details-offset',
      `calc(100dvh - ${Math.floor(
        parentTop + detailsTop - this.heightOffset
      )}px)`
    )
  }

  broadcast() {
    const eventType = this.hasOpenClass ? 'modalOpened' : 'modalClosed'
    this.dispatchEvent(
      new CustomEvent(eventType, {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  onTriggerKeydown(e) {
    if (e.code === 'Enter' || e.code === ' ') {
      e.preventDefault()
      this.toggle(e)
    }
  }

  onFocus(e) {
    if (this.mousedown) {
      this.focused = false
    } else {
      this.focused = true
    }
  }

  onMouseDown(e) {
    this.mousedown = true
  }

  onFocusOut(e) {
    this.focused = false
    this.mousedown = false
  }

  render() {
    return html`
      <div
        class="mega-menu-modal ${classMap({ 'is-open': this.hasOpenClass })}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <div
          class="mega-menu-modal__trigger"
          role="button"
          tabindex="0"
          aria-expanded="${this.open}"
          aria-controls="hh-mega-menu-modal-panel"
          aria-label="${this.label || 'Menu'}"
          part="summary"
          @click="${this.toggle}"
          @keydown="${this.onTriggerKeydown}"
          @focusin="${this.onFocus}"
          @focusout="${this.onFocusOut}"
          @mousedown="${this.onMouseDown}"
        >
          <slot
            class="${classMap({ 'is-open': this.hasOpenClass })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </div>
        <div
          class="content"
          id="hh-mega-menu-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.open}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </div>`
  }

  static styles = css`
    .mega-menu-modal {
      /* No position: relative — .content (position: absolute) must be positioned
         relative to a wider ancestor (e.g. header), not this wrapper inside <li>. */
    }

    .mega-menu-modal__trigger {
      position: relative;
      z-index: 2;
      touch-action: manipulation;
      list-style: none;
    }

    .mega-menu-modal__trigger:focus {
      outline: 0;
      outline-offset: var(--hh-focus-outline-offset);
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-color-reverse);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
      opacity: 0;
      visibility: hidden;
      overflow-y: auto;
      pointer-events: none;
      transition:
        opacity var(--hh-duration-default, 300ms),
        visibility var(--hh-duration-default, 300ms);
    }

    .is-open .content {
      visibility: visible;
      opacity: 1;
      pointer-events: auto;
    }

    :host .content {
      visibility: visible;
      transition-delay: ${HOVER_CLOSE_DELAY_MS}ms;
    }

    :host .is-open .content {
      transition: opacity ${HOVER_CLOSE_DURATION_MS}ms;
      transition-delay: 0s;
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
    }

    :host([data-panel-open]) {
      z-index: 101;
    }

    :host([focused='true']) .mega-menu-modal__trigger {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: 0;
    }

    @media screen and (min-width: 990px) {
      .content {
        overflow-y: visible;
        background: transparent;
      }
    }
  `
}

customElements.define('hh-mega-menu-modal', HHMegaMenuModal)
